import { useEffect, useState } from "react";
import Container from "@/components/Container";
import { api } from "@/lib/api";
import { ADMIN } from "@/constants/testIds";
import { toast } from "sonner";

const ROOMS = ["Exterior","Entrance","Living room","Kitchen","Dining","Bedrooms","Bathrooms","Garden","Garage","Roof","Windows/doors","Heating/electrics/plumbing","Structural","Decorating","Staging"];

export default function Projects() {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ project_name: "", renovation_budget: 0, status: "planning" });
  const [active, setActive] = useState(null);

  const load = () => api.get("/projects").then(r => setItems(r.data || []));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.project_name) return toast.error("Name required");
    await api.post("/projects", { ...form, renovation_budget: Number(form.renovation_budget) || 0 });
    toast.success("Created"); setShowForm(false); setForm({ project_name: "", renovation_budget: 0, status: "planning" }); load();
  };
  const update = async (id, patch) => { await api.put(`/projects/${id}`, patch); load(); if (active?.id === id) setActive({...active, ...patch}); };

  return (
    <Container className="py-8 md:py-12">
      <div className="flex items-end justify-between gap-3">
        <div>
          <p className="overline">Deals</p>
          <h1 className="mt-2 text-4xl md:text-5xl font-serif text-ivory">Renovation Projects</h1>
        </div>
        <button onClick={() => setShowForm(s => !s)} className="btn-ruby" data-testid={ADMIN.createProject}>{showForm ? "Cancel" : "New Project"}</button>
      </div>

      {showForm && (
        <form onSubmit={create} className="luxury-card p-6 mt-6 grid grid-cols-1 md:grid-cols-3 gap-3">
          <input className="bg-charcoal-800 border border-charcoal-600 px-3 py-2 text-sm md:col-span-2" placeholder="Project name" value={form.project_name} onChange={e => setForm({...form, project_name: e.target.value})} />
          <input className="bg-charcoal-800 border border-charcoal-600 px-3 py-2 text-sm" type="number" placeholder="Renovation budget" value={form.renovation_budget} onChange={e => setForm({...form, renovation_budget: e.target.value})} />
          <button className="btn-ruby md:col-span-3 justify-center">Create Project</button>
        </form>
      )}

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="luxury-card divide-y divide-charcoal-600/60 max-h-[70vh] overflow-y-auto">
          {items.length === 0 && <p className="p-8 text-center text-ivory/40 text-sm">No projects.</p>}
          {items.map(p => (
            <button key={p.id} onClick={() => setActive(p)} className={`w-full text-left p-4 hover:bg-charcoal-800/50 ${active?.id===p.id ? "bg-charcoal-800" : ""}`} data-testid={`project-${p.id}`}>
              <p className="text-ivory">{p.project_name}</p>
              <p className="text-xs text-ivory/50 mt-1">{p.status} · £{(p.renovation_budget||0).toLocaleString()} budget</p>
              <div className="mt-2 h-1 bg-charcoal-600">
                <div className="h-full bg-gold" style={{ width: `${p.completion_percentage || 0}%` }} />
              </div>
            </button>
          ))}
        </div>

        <div className="lg:col-span-2 luxury-card p-6">
          {!active ? <p className="text-ivory/40 text-sm">Select a project to edit.</p> : (
            <div className="space-y-5">
              <div>
                <p className="overline">{active.status}</p>
                <h2 className="mt-1 text-3xl font-serif text-ivory">{active.project_name}</h2>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <Stat k="Budget" v={`£${(active.renovation_budget||0).toLocaleString()}`} />
                <Stat k="Spent" v={`£${(active.actual_spend||0).toLocaleString()}`} />
                <Stat k="Complete" v={`${active.completion_percentage||0}%`} />
                <Stat k="Listed" v={active.listed_resale_price ? `£${active.listed_resale_price.toLocaleString()}` : "—"} />
              </div>
              <div>
                <p className="overline">Update Status</p>
                <select value={active.status} onChange={e => update(active.id, { status: e.target.value })} className="mt-2 w-full bg-charcoal-800 border border-charcoal-600 px-3 py-2 text-sm">
                  {["planning","in_progress","snagging","staging","complete","sold"].map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <p className="overline">Completion %</p>
                <input type="range" min={0} max={100} value={active.completion_percentage || 0} onChange={e => update(active.id, { completion_percentage: Number(e.target.value) })} className="w-full mt-2 accent-ruby" />
              </div>
              <div>
                <p className="overline">Room sections</p>
                <div className="mt-2 grid grid-cols-2 md:grid-cols-3 gap-1.5">
                  {ROOMS.map(r => <span key={r} className="text-[0.65rem] uppercase tracking-[0.15em] border border-charcoal-600 px-2 py-1 text-ivory/60">{r}</span>)}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Container>
  );
}

function Stat({ k, v }) {
  return <div className="bg-charcoal-800 border border-charcoal-600 p-3"><p className="text-[0.6rem] uppercase tracking-[0.15em] text-ivory/40">{k}</p><p className="text-ivory mt-1">{v}</p></div>;
}
